import { useState } from 'react';
import { inAppBrowser, iosNotSafari, isAndroid, isIOS, isSamsungBrowser } from '@/lib/device';

// How to put CMB Chats on the home screen, worded for the browser in hand (Gary, 19 Oct: recent iPhones keep Share
// under the ••• button, Samsung Internet's menu is ≡ at the bottom right). Shared by sign-in and set-up.

/** Inside WhatsApp, Telegram, Instagram or Facebook, or Chrome on an iPhone: the way out, with the link to copy. */
export function OpenInBrowser() {
  const [copied, setCopied] = useState<'no' | 'yes' | 'failed'>('no');
  if (!inAppBrowser && !iosNotSafari) return null;
  const link = `${window.location.origin}${import.meta.env.BASE_URL}`;
  // Some in-app browsers block copying: the link is always shown to select by hand (review, 19 Oct).
  const copy = () => {
    if (!navigator.clipboard) { setCopied('failed'); return; }
    void navigator.clipboard.writeText(link).then(() => setCopied('yes'), () => setCopied('failed'));
  };
  return (
    <div className="chats-setup-warn">
      {isIOS ? (
        <p><b>This must open in Safari.</b> Copy the link, open <b>Safari</b>, paste it in the address bar and go.</p>
      ) : (
        <p><b>Open this in Chrome.</b> Tap <b>⋮</b> at the top right, then <b>Open in Chrome</b>. Or copy the link and paste it into Chrome.</p>
      )}
      <p><b>{link}</b></p>
      {copied === 'failed' && <p>Copying is blocked here. Press and hold the link above to copy it.</p>}
      <button type="button" className="chats-btn chats-step-btn" onClick={copy}>{copied === 'yes' ? 'Link copied' : 'Copy link'}</button>
    </div>
  );
}

/** The steps for this browser. Android's are optional: alerts work in Chrome without it. */
export function HomeScreenSteps({ canInstall, install }: { canInstall?: boolean; install?: () => void }) {
  // The way out of an in-app browser shows once, at the top of the screen (OpenInBrowser), not here as well.
  if (inAppBrowser || iosNotSafari) return null;
  if (isIOS) {
    return (
      <ol className="chats-setup-howto">
        <li>In Safari, tap <b>•••</b> at the bottom right, or the <b>Share</b> button (the square with the arrow).</li>
        <li>Tap <b>Share</b> if you see it, then scroll down and tap <b>Add to Home Screen</b>. Leave <b>Open as Web App</b> on, then tap <b>Add</b>.</li>
        <li>Close Safari and open <b>CMB Chats</b> from your home screen. It opens with no address bar.</li>
      </ol>
    );
  }
  if (canInstall && install) {
    return <button type="button" className="chats-btn chats-step-btn" onClick={install}>Add to home screen</button>;
  }
  if (isAndroid && isSamsungBrowser) {
    return (
      <ol className="chats-setup-howto">
        <li>Tap <b>≡</b> at the bottom right.</li>
        <li>Tap <b>Add page to</b>, then <b>Home screen</b>.</li>
      </ol>
    );
  }
  return (
    <ol className="chats-setup-howto">
      <li>Tap <b>⋮</b> at the top right of Chrome.</li>
      <li>Tap <b>Add to Home screen</b> or <b>Install app</b>.</li>
    </ol>
  );
}
